import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';

import styled from '@emotion/styled';

import { useCheckVerificationsComplete } from 'hooks/useCheckVerificationsComplete';
import { useGetRevenuesMetrics } from 'services/hooks/revenue';

import ButtonGroup from 'components/buttonGroup/ButtonGroup';
import Card from 'components/card/Card';
import { OutletContextDeviceType } from 'components/layout/model';
import Skeleton from 'components/loading/Skeleton';
import withErrorBoundary from 'components/provider/withErrorBoundary';
import TabList from 'components/tabList/TabList';
import AwaitQuery, { RefetchFn } from 'components/util/AwaitQuery';
import Flex from 'components/util/Flex';

import { useRevenueMetricsContext } from 'pages/dashboard/hooks/useRevenueMetricsContext';

import RevenueChart from './RevenueChart';
import { AdditionalLoading } from '../shared/AdditionalLoading';

function RevenueChartContainer() {
  const { t } = useTranslation();
  const { device } = useOutletContext<OutletContextDeviceType>();
  const { period, sourceType } = useRevenueMetricsContext();

  const queryResult = useGetRevenuesMetrics({
    // Entire의 경우 sourceType을 보내지 않도록 해야함
    sourceType: sourceType === 'entire' ? undefined : sourceType,
    period: period,
  });
  const rowGap = device.isDesktop ? 16 : 24;
  const { isAllVerificationCompleted } = useCheckVerificationsComplete();

  return (
    <Card isExpanded>
      <Flex direction="column" rowGap={rowGap}>
        {/* HEADER */}
        <Card.Header>
          <Flex direction="column" rowGap={rowGap}>
            <Flex isExpanded justify={device.isDesktop ? 'center' : 'flex-start'}>
              <TabList />
            </Flex>
            <Flex columnGap={8}>
              <Card.Title isExpanded>{t('chat.rewards_tracking_revenue')}</Card.Title>
              <ButtonGroup />
            </Flex>
          </Flex>
        </Card.Header>
        {/* Body */}
        <SRevenueCardBody>
          <AwaitQuery
            queryResult={queryResult}
            // initialLoadingComponent={() => <SInitialLoading />}
            initialLoadingComponent={() => <Skeleton height={'304px'} />}
            additionalLoadingComponent={() => <AdditionalLoading height={'304px'} />}
            errorComponent={({ refetch, error }: ErrorComponentProps) => (
              <ErrorComponent error={error} refetch={refetch} />
            )}
          >
            <RevenueChart data={queryResult.data!} isBankAccountVerificationCompleted={isAllVerificationCompleted} />
          </AwaitQuery>
        </SRevenueCardBody>
        <SLegend>
          <SLegendItem>
            <SLegendBar $color="primary" />
            <span>{t('statistic_domestic')}</span>
          </SLegendItem>
          <SLegendItem>
            <SLegendBar $color="secondary" />
            <span>{t('statistic_international')}</span>
          </SLegendItem>
        </SLegend>
      </Flex>
    </Card>
  );
}

const SRevenueCardBody = styled.div`
  position: relative;
`;

const SLegend = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const SLegendItem = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;

  span {
    color: ${({ theme }) => theme.color.text.TXT_1};
    ${({ theme }) => theme.font.caption1_r};
  }
`;

const SLegendBar = styled.span<{ $color: 'primary' | 'secondary' }>`
  width: 32px;
  height: 6px;
  border-radius: 30px;
  // 바와 동일한 브랜드색 — 팅글 라임/마젠타, 버블챗 시안/보라
  background: ${({ theme, $color }) => ($color === 'primary' ? theme.color.static.primary : theme.color.static.secondary)};
`;

const SInitialLoading = styled.div`
  width: 100%;
  height: 304px;
`;

type ErrorComponentProps = {
  error: any;
  refetch: RefetchFn;
};
const ErrorComponent = ({ error, refetch }: ErrorComponentProps) => {
  return (
    <SErrorComponent>
      <button onClick={() => refetch()}>Retry</button>
    </SErrorComponent>
  );
};
const SErrorComponent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 304px;
  background-color: rgba(255, 255, 255, 0.6);
  z-index: 100;
`;

export default withErrorBoundary(RevenueChartContainer);
